import React from 'react';
import { Box, Button, Grid, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Property } from '../../models/property';

interface PropertyEditDetailsDisplayProps {
  property: Property;
  onSave: (updatedProperty: Property) => void;
  onCancel: () => void;
  propertyId: string;
}

const PropertyEditDetailsDisplay: React.FC<PropertyEditDetailsDisplayProps> = ({ property, onSave, onCancel, propertyId}) => {
  const [editedProperty, setEditedProperty] = React.useState<Property>(property);

  const handleInputChange = (field: keyof Property, value: string | number | boolean) => {
    setEditedProperty({ ...editedProperty, [field]: value });
  };

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      {/* Address and Description */}
      <TextField
        fullWidth
        label="Address"
        variant="outlined"
        value={editedProperty.address}
        onChange={(e) => handleInputChange('address', e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Description"
        variant="outlined"
        value={editedProperty.propertyDescription}
        onChange={(e) => handleInputChange('propertyDescription', e.target.value)}
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      {/* Numeric Fields */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Property Value"
            variant="outlined"
            type="number"
            value={editedProperty.propertyValue || ''}
            onChange={(e) => handleInputChange('propertyValue', Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Square Footage"
            variant="outlined"
            type="number"
            value={editedProperty.squareFootage || ''}
            onChange={(e) => handleInputChange('squareFootage', Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={4} md={4}>
          <TextField
            fullWidth
            label="Bedrooms"
            variant="outlined"
            type="number"
            value={editedProperty.bedrooms || ''}
            onChange={(e) => handleInputChange('bedrooms', Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={4} md={4}>
          <TextField
            fullWidth
            label="Bathrooms"
            variant="outlined"
            type="number"
            value={editedProperty.bathrooms || ''}
            onChange={(e) => handleInputChange('bathrooms', Number(e.target.value))}
          />
        </Grid>
        <Grid item xs={4} md={4}>
          <TextField
            fullWidth
            label="Units"
            variant="outlined"
            type="number"
            value={editedProperty.units || ''}
            onChange={(e) => handleInputChange('units', Number(e.target.value))}
            disabled={!editedProperty.multiFamily}
          />
        </Grid>
      </Grid>

      {/* Multi-family Flag */}
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!editedProperty.multiFamily}
              onChange={(e) => handleInputChange('multiFamily', e.target.checked)}
            />
          }
          label="Is Multi-Family Property"
        />
      </Box>

      {/* Save/Cancel Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button variant="contained" color="primary" onClick={() => onSave(editedProperty)} fullWidth sx={{ mr: 1 }}>
          Save
        </Button>
        <Button variant="outlined" color="secondary" onClick={onCancel} fullWidth>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default PropertyEditDetailsDisplay;
